import React, { useContext, useRef, useState } from 'react';

import { useToasts } from 'react-toast-notifications';

import Button from '@components/common/Button/Button';
import useOnClickOutside from '@components/common/hooks/useOnClickOutside';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import IconCheckmark from '@components/common/IconCheckmark/IconCheckmark';
import ConfirmModal from '@components/common/Modals/ConfirmModal/ConfirmModal';
import MoreIcon from '@components/common/MoreIcon/MoreIcon';
import PotContext from '@components/lpc/Context/Pot/PotContext';
import { personRoles } from '@models/PotModel/personRoles';
import notificationService from '@services/domain/Lpc/NotificationsService';
import PotService from '@services/domain/Lpc/PotService';
import { formatCurrency } from '@services/utils/StringService';

import css from './NotificationAction.module.scss';

type NotificationActionProps = {
    potRef: string;
    personRole: string;
    orderId: string;
    lastName: string;
    firstName: string;
    type: string;
    liked?: boolean;
    counter?: number;
    contribution: number;
    id: string;
    onUpdateActivities: () => void;
    setOpenParticipantModal?: (value: boolean) => void;
};

const NotificationAction = ({
    type,
    personRole,
    orderId,
    contribution,
    lastName,
    firstName,
    id,
    onUpdateActivities,
    potRef,
    setOpenParticipantModal,
}: NotificationActionProps) => {
    const { t } = useTranslateLpc(['lpc-pot', 'common']);
    const { addToast } = useToasts();
    const [refundLoading, setRefundLoading] = useState<boolean>(false);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
    const potContext = useContext(PotContext);
    const { getUpdatedPot } = potContext;
    const showRefundButton: boolean = orderId != 'LPC_PROMO_CODE';

    const actionMoreRef = useRef();
    useOnClickOutside(actionMoreRef, () => setIsPopUpOpen(false));

    const [isPopUpOpen, setIsPopUpOpen] = useState(false);

    const onRefund = () => {
        setRefundLoading(true);
        return PotService.refundParticipant(potRef, orderId)
            .then(() => {
                onUpdateActivities();
                getUpdatedPot(potRef);
                setOpenParticipantModal(false);
            })
            .catch(() =>
                addToast(t(`errors.DEFAULT_ERROR_MESSAGE`), {
                    appearance: 'error',
                    autoDismiss: true,
                }),
            )
            .finally(() => setRefundLoading(false));
    };

    const onDeleteMessage = (uniqPA) => {
        setDeleteLoading(true);
        notificationService
            .deleteActivity(potRef, uniqPA)
            .then(() => {
                onUpdateActivities();
            })
            .catch((err) => err)
            .finally(() => setDeleteLoading(false));
    };

    return (
        <div ref={actionMoreRef} className={css.notification__action}>
            {personRole === personRoles.ORGANISER && (
                <MoreIcon
                    customClassContainer={css.notification__action__more}
                    customClassIcon={css.notification__action__more__icon}
                    onOpen={() => setIsPopUpOpen(true)}
                    icon={'/front-static/icons/action/more-horizontal-primary.svg'}
                />
            )}

            <div className={`${css.popup} ${isPopUpOpen && css.popup__open}`}>
                {type === 'CONTRIBUTION' && (
                    <>
                        {' '}
                        {showRefundButton && (
                            <Button
                                variant={'white'}
                                customClass={css.cta}
                                onClick={() => setShowCancelConfirmModal(true)}
                            >
                                {t('activity.refund-message')}
                            </Button>
                        )}
                        <ConfirmModal
                            title={t('activity.refund.title')}
                            text={t('activity.refund.text', {
                                participant: firstName + ' ' + lastName,
                                amount: formatCurrency(contribution),
                            })}
                            iconComponent={<IconCheckmark variant={'warning'} />}
                            confirmLabel={t('activity.refund.cta')}
                            cancelLabel={t('activity.refund.cancel')}
                            isVisible={showCancelConfirmModal}
                            onHide={() => setShowCancelConfirmModal(false)}
                            isLoading={refundLoading}
                            onConfirm={() => onRefund()}
                        />
                    </>
                )}
                {type === 'MESSAGE' && (
                    <Button
                        isLoading={deleteLoading}
                        variant={'white'}
                        customClass={css.cta}
                        onClick={() => onDeleteMessage(id)}
                    >
                        {t('activity.delete-message')}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default NotificationAction;
